import React from 'react'
import { Clock, UserPlus, UserCheck, UserX, Calendar, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { useLocale } from '../contexts/LocaleContext'

export interface ActivityItem {
  id: string
  type: 'shift_claimed' | 'shift_cancelled' | 'shift_completed' | 'volunteer_applied' | 'volunteer_approved' | 'volunteer_rejected' | 'profile_updated'
  timestamp: string
  volunteer_name: string
  volunteer_email: string
  volunteer_avatar?: string | null
  description: string
  details?: string
  metadata?: {
    shift_title?: string
    shift_time?: string
    shift_location?: string
    request_motivation?: string
    status?: string
  }
}

interface VolunteerActivityTimelineProps {
  activities: ActivityItem[]
  loading?: boolean
}

const VolunteerActivityTimeline: React.FC<VolunteerActivityTimelineProps> = ({
  activities,
  loading = false
}) => {
  const { t } = useLocale()

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'shift_claimed':
        return <Calendar className="h-4 w-4 text-blue-500" />
      case 'shift_cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'shift_completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'volunteer_applied':
        return <UserPlus className="h-4 w-4 text-purple-500" />
      case 'volunteer_approved':
        return <UserCheck className="h-4 w-4 text-green-500" />
      case 'volunteer_rejected':
        return <UserX className="h-4 w-4 text-red-500" />
      case 'profile_updated':
        return <AlertCircle className="h-4 w-4 text-orange-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getActivityDescription = (activity: ActivityItem) => {
    // Convert snake_case to camelCase for translation keys
    const translationKey = activity.type.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    const baseDescription = t(`dashboard.activityTypes.${translationKey}`)
    
    if (activity.metadata?.shift_title) {
      const shiftDetails = `Dienst: ${activity.metadata.shift_title} op ${activity.metadata.shift_time || ''}`
      return `${baseDescription} - ${shiftDetails}`
    }
    
    if (activity.metadata?.request_motivation) {
      const motivationDetails = `Motivatie: ${activity.metadata.request_motivation.substring(0, 100)}${activity.metadata.request_motivation.length > 100 ? '...' : ''}`
      return `${baseDescription} - ${motivationDetails}`
    }
    
    return baseDescription
  }

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'shift_claimed':
        return 'border-blue-200 bg-blue-50'
      case 'shift_cancelled':
        return 'border-red-200 bg-red-50'
      case 'shift_completed':
        return 'border-green-200 bg-green-50'
      case 'volunteer_applied':
        return 'border-purple-200 bg-purple-50'
      case 'volunteer_approved':
        return 'border-green-200 bg-green-50'
      case 'volunteer_rejected':
        return 'border-red-200 bg-red-50'
      case 'profile_updated':
        return 'border-orange-200 bg-orange-50'
      default:
        return 'border-gray-200 bg-gray-50'
    }
  }

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {t('dashboard.recentActivity')}
          </h3>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="flex items-start space-x-3">
                  <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {t('dashboard.recentActivity')}
          </h3>
          <div className="text-center py-8">
            <Clock className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('dashboard.noRecentActivity')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('dashboard.noActivityDescription')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
          {t('dashboard.recentActivity')}
        </h3>
        <div className="flow-root">
          <ul className="-mb-8">
            {activities.map((activity, activityIdx) => (
              <li key={activity.id}>
                <div className="relative pb-8">
                  {activityIdx !== activities.length - 1 ? (
                    <span
                      className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                      aria-hidden="true"
                    />
                  ) : null}
                  <div className="relative flex space-x-3">
                    <div className="flex-shrink-0">
                      <div className={`h-8 w-8 rounded-full border-2 flex items-center justify-center ${getActivityColor(activity.type)}`}>
                        {getActivityIcon(activity.type)}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <div className="flex-shrink-0">
                            {activity.volunteer_avatar ? (
                              <img
                                className="h-6 w-6 rounded-full"
                                src={activity.volunteer_avatar}
                                alt={activity.volunteer_name}
                              />
                            ) : (
                              <div className="h-6 w-6 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600">
                                  {activity.volunteer_name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            {activity.volunteer_name}
                          </p>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">
                          {getActivityDescription(activity)}
                        </p>
                        {activity.details && (
                          <p className="mt-1 text-xs text-gray-500">
                            {activity.details}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm whitespace-nowrap text-gray-500">
                        <time dateTime={activity.timestamp}>
                          {new Date(activity.timestamp).toLocaleDateString('nl-NL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </time>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default VolunteerActivityTimeline
